import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CreatePreRegistrationDto,
  CreateMarketingConsentDto,
  PreRegistrationService,
  MarketingConsentService,
  EventService,
  EventDto,
} from '@millionshow/api-client';
import {
  ToastService,
  PageModalContainer,
  ModalService,
} from '@millionshow/client-libs';
import { of } from 'rxjs';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  AwarenessChannel,
  normalizeRegistrationFormConfig,
  RegistrationFormConfig,
  RegistrationFormFieldConfig,
  RegistrationVisitMotiveResponse,
  RegistrationVisitMotiveSelectionMode,
  REGISTRATION_AGE_GROUP_LABELS,
  REGISTRATION_GENDER_LABELS,
  REGISTRATION_REGION_GROUP_OPTION_LABELS,
  RegistrationAgeGroup,
  RegistrationGender,
  RegistrationRegionGroup,
} from '@millionshow/common';
import { RegistrationCompleteModal } from '../registration/modals/registration-complete.modal';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { PolicyModal } from '../../modals/policy/policy.modal';
import { RegistrationInfoComponent } from './components/registration-info.component';
import { RegistrationPriceInfoComponent } from './components/registration-price-info.component';
import { RegistrationActionButtonsComponent } from './components/registration-action-buttons.component';
import { SeoService } from '../../services/seo.service';

@Component({
  selector: 'app-registration-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PageModalContainer,
    RegistrationInfoComponent,
    RegistrationPriceInfoComponent,
    RegistrationActionButtonsComponent,
  ],
  templateUrl: './registration-form.page.html',
})
export default class RegistrationFormPage {
  private readonly preRegistrationService = inject(PreRegistrationService);
  private readonly marketingConsentService = inject(MarketingConsentService);
  private readonly eventService = inject(EventService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly seoService = inject(SeoService);

  readonly queryEventId = injectQueryParams<string>('eventId');

  allAgreed = signal<boolean>(false);
  isTermsAgreed = signal<boolean>(false);
  isPrivacyAgreed = signal<boolean>(false);
  awarenessTouched = signal<boolean>(false);

  readonly genderOptions = Object.entries(REGISTRATION_GENDER_LABELS).map(
    ([key, label]) => ({
      value: key as RegistrationGender,
      label,
    })
  );

  readonly ageGroupOptions = Object.entries(REGISTRATION_AGE_GROUP_LABELS).map(
    ([key, label]) => ({
      value: key as RegistrationAgeGroup,
      label,
    })
  );

  readonly regionGroupOptions = Object.entries(
    REGISTRATION_REGION_GROUP_OPTION_LABELS
  ).map(([key, label]) => ({
    value: key as RegistrationRegionGroup,
    label,
  }));

  form = new FormGroup({
    eventId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    contact: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    gender: new FormControl<RegistrationGender | null>(null, {
      validators: [Validators.required],
    }),
    ageGroup: new FormControl<RegistrationAgeGroup | null>(null, {
      validators: [Validators.required],
    }),
    regionGroup: new FormControl<RegistrationRegionGroup | null>(null, {
      validators: [Validators.required],
    }),
    visitMotives: new FormControl<string[]>([], {
      nonNullable: true,
    }),
  });

  readonly eventIdValueSignal = toSignal(
    this.form.controls.eventId.valueChanges
  );

  readonly selectedMotives = toSignal(
    this.form.controls.visitMotives.valueChanges,
    { initialValue: [] as string[] }
  );

  $events = rxResource({
    stream: () =>
      this.eventService.eventControllerFindAllV1({
        status: 'ONGOING',
        type: 'MILLIONSHOW' as const,
      }),
  });

  events = computed(() => {
    const items = this.$events.value() || [];
    return [...items].sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
    );
  });

  $event = rxResource({
    stream: ({ params }) => {
      if (!params.eventId) {
        return of(undefined);
      }

      return this.eventService.eventControllerFindByIdV1({
        id: params.eventId,
      });
    },
    params: () => ({ eventId: this.eventIdValueSignal() }),
  });

  event = computed(() => {
    const eventData = this.$event.value() as EventDto | undefined;
    return eventData;
  });

  registrationFormConfig = computed(() =>
    normalizeRegistrationFormConfig(
      (
        this.event() as {
          registrationFormConfig?: Partial<RegistrationFormConfig> | null;
        }
      )?.registrationFormConfig
    )
  );

  genderConfig = computed(() => this.registrationFormConfig().fields.gender);
  ageGroupConfig = computed(
    () => this.registrationFormConfig().fields.ageGroup
  );
  regionGroupConfig = computed(
    () => this.registrationFormConfig().fields.regionGroup
  );
  visitMotivesConfig = computed(
    () => this.registrationFormConfig().fields.visitMotives
  );

  readonly awarenessOptions = computed(() =>
    this.visitMotivesConfig()
      .options.filter(option => option.enabled)
      .map(option => ({
        value: option.id,
        label: option.label,
      }))
  );

  constructor() {
    this.seoService.set({
      title: '사전등록 신청서',
      description: '밀리언쇼 사전등록 신청서를 작성하고 접수하세요.',
      path: '/registration-form',
      robots: 'noindex, follow',
    });

    effect(() => {
      const eventId = this.queryEventId();
      if (eventId && !this.form.controls.eventId.value) {
        this.form.controls.eventId.setValue(eventId);
      }
    });

    effect(() => {
      this.applyRegistrationFormConfig(this.registrationFormConfig());
    });

    effect(() => {
      const event = this.event();

      if (!event?.title) return;

      this.seoService.set({
        title: `${event.title} 사전등록`,
        description: `${event.title} 밀리언쇼 사전등록을 진행하세요.`,
        path: '/registration-form',
        robots: 'noindex, follow',
      });
    });

    effect(() => {
      const terms = this.isTermsAgreed();
      const privacy = this.isPrivacyAgreed();

      if (terms && privacy) {
        this.allAgreed.set(true);
      } else if (this.allAgreed()) {
        this.allAgreed.set(false);
      }
    });
  }

  toggleAllAgreed(checked: boolean): void {
    this.allAgreed.set(checked);
    this.isTermsAgreed.set(checked);
    this.isPrivacyAgreed.set(checked);
  }

  private applyRegistrationFormConfig(config: RegistrationFormConfig): void {
    this.applyFieldConfig(this.form.controls.gender, config.fields.gender);
    this.applyFieldConfig(this.form.controls.ageGroup, config.fields.ageGroup);
    this.applyFieldConfig(
      this.form.controls.regionGroup,
      config.fields.regionGroup
    );

    const visitMotivesConfig = config.fields.visitMotives;
    const currentMotives = this.form.controls.visitMotives.value.filter(
      motive =>
        visitMotivesConfig.enabled &&
        visitMotivesConfig.options.some(
          option => option.enabled && option.id === motive
        )
    );
    const nextMotives =
      visitMotivesConfig.selectionMode ===
      RegistrationVisitMotiveSelectionMode.SINGLE
        ? currentMotives.slice(0, 1)
        : currentMotives;

    this.form.controls.visitMotives.setValue(nextMotives, {
      emitEvent: false,
    });
  }

  private applyFieldConfig<T>(
    control: AbstractControl<T | null>,
    config: RegistrationFormFieldConfig
  ): void {
    if (config.enabled && config.required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }

    if (!config.enabled) {
      control.setValue(null as T | null, { emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  selectGender(gender: RegistrationGender): void {
    this.form.controls.gender.setValue(gender);
  }

  selectAgeGroup(ageGroup: RegistrationAgeGroup): void {
    this.form.controls.ageGroup.setValue(ageGroup);
  }

  selectRegionGroup(regionGroup: RegistrationRegionGroup): void {
    this.form.controls.regionGroup.setValue(regionGroup);
  }

  toggleAwareness(optionId: string): void {
    this.awarenessTouched.set(true);
    const current = this.form.value.visitMotives || [];
    const index = current.indexOf(optionId);

    if (
      this.visitMotivesConfig().selectionMode ===
      RegistrationVisitMotiveSelectionMode.SINGLE
    ) {
      this.form.patchValue({ visitMotives: index > -1 ? [] : [optionId] });
      return;
    }

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(optionId);
    }

    this.form.patchValue({ visitMotives: [...current] });
  }

  private isVisitMotivesValid(): boolean {
    const config = this.visitMotivesConfig();
    return (
      !config.enabled || !config.required || this.selectedMotives().length > 0
    );
  }

  hasAwarenessError(): boolean {
    return (
      this.visitMotivesConfig().enabled &&
      this.visitMotivesConfig().required &&
      this.awarenessTouched() &&
      this.selectedMotives().length === 0
    );
  }

  canSubmit(): boolean {
    return (
      this.form.valid && this.isVisitMotivesValid() && this.isTermsAgreed()
    );
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  submit(): void {
    this.awarenessTouched.set(true);

    if (!this.canSubmit()) {
      if (!this.isTermsAgreed()) {
        this.toastService.error('개인정보 이용에 동의해주세요.');
      } else if (!this.isVisitMotivesValid()) {
        this.toastService.error('관람동기를 선택해주세요.');
      } else {
        this.toastService.error('필수 항목을 입력해주세요.');
      }
      return;
    }

    const formValue = this.form.getRawValue();
    const normalizedContact = formValue.contact.replace(/[^0-9]/g, '');
    const visitMotiveResponses =
      this.isPrivacyAgreed() && this.visitMotivesConfig().enabled
        ? this.buildVisitMotiveResponses(formValue.visitMotives)
        : [];
    const visitMotives = visitMotiveResponses
      .map(response => response.channel)
      .filter((channel): channel is AwarenessChannel => Boolean(channel));

    const marketingBody: CreateMarketingConsentDto = {
      contact: normalizedContact,
      name: formValue.name,
      interestedRegions: [],
      visitMotives: visitMotives,
      visitMotiveResponses,
      isAgreed: this.isPrivacyAgreed(),
      sourceType: 'PRE_REGISTRATION',
      brandType: 'MILLIONSHOW',
    };

    this.marketingConsentService
      .marketingConsentControllerCreateV1({ body: marketingBody })
      .subscribe({
        next: marketing => {
          this.submitPreRegistration(marketing.id);
        },
        error: err => {
          const message =
            err?.error?.message ||
            err?.message ||
            '마케팅 동의 처리 중 오류가 발생했습니다.';
          this.toastService.error({ message });
        },
      });
  }

  private buildVisitMotiveResponses(
    selectedOptionIds: string[]
  ): RegistrationVisitMotiveResponse[] {
    const optionMap = new Map(
      this.visitMotivesConfig().options.map(option => [option.id, option])
    );

    return selectedOptionIds.flatMap(optionId => {
      const option = optionMap.get(optionId);

      return option
        ? [
            {
              id: option.id,
              label: option.label,
              channel: option.channel,
            },
          ]
        : [];
    });
  }
  private submitPreRegistration(marketingId?: string): void {
    const formValue = this.form.getRawValue();

    const body: CreatePreRegistrationDto = {
      name: formValue.name,
      contact: formValue.contact.replace(/[^0-9]/g, ''),
      eventId: formValue.eventId,
      marketingId: marketingId,
      gender: this.genderConfig().enabled
        ? formValue.gender ?? undefined
        : undefined,
      ageGroup: this.ageGroupConfig().enabled
        ? formValue.ageGroup ?? undefined
        : undefined,
      regionGroup: this.regionGroupConfig().enabled
        ? formValue.regionGroup ?? undefined
        : undefined,
      sourceType: 'MILLIONSHOW',
    };

    this.preRegistrationService
      .preRegistrationControllerCreateV1({ body })
      .subscribe({
        next: () => {
          const modal = this.modalService.create(RegistrationCompleteModal, {
            componentProps: {
              eventTitle: this.event()?.title || '',
              name: formValue.name,
              contact: formValue.contact,
            },
          });
          modal.closed.subscribe(() => {
            this.router.navigate(['/']);
          });
        },
        error: err => {
          const message =
            err?.error?.message ||
            err?.message ||
            '등록 중 오류가 발생했습니다.';

          this.toastService.error({ message });
        },
      });
  }

  openPolicyModal(type: 'use' | 'privacy' | 'marketing'): void {
    this.modalService.create(PolicyModal, {
      componentProps: { type },
    });
  }
}
